import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Dialog,
  DialogScenes,
  Script,
} from 'src/replicant/conversation/conservation.ruleset';
import StateObserver from '../../StateObserver';
import statePromise from '../../lib/statePromise';
import PopupMonitor from '../logic/PopupMonitor';
import { getRandomItemFromArray } from 'src/replicant/utils/random';
import { RecordOfAllBut, WithId } from 'src/replicant/utils/types';
import { Conversation } from 'src/replicant/conversation';
import { PetSpecies, PetState } from 'src/replicant/buruburu/pet';
import platform from '@play-co/gcinstant';

type ActiveDialog = Dialog['id'] | null;
type ActiveScript = Script['id'] | null;

const initialConversationState = {
  activeDialog: null as ActiveDialog,
  activeScript: null as ActiveScript,
};

export type ConversationState = typeof initialConversationState;

const initialState: RecordOfAllBut<
  string,
  ConversationState,
  keyof ConversationState
> = {};

const { actions, reducer } = createSlice({
  name: 'conversation',

  initialState,

  reducers: {
    setActiveDialog: (
      conversation,
      { payload: { id, payload } }: PayloadAction<WithId<ActiveDialog>>,
    ) => {
      if (!conversation[id]) {
        return;
      }
      conversation[id].activeDialog = payload;
    },

    setActiveScript: (
      conversation,
      { payload: { id, payload } }: PayloadAction<WithId<ActiveScript>>,
    ) => {
      if (!conversation[id]) {
        return;
      }
      conversation[id].activeScript = payload;
    },

    createConversation: (
      conversation,
      {
        payload: { id, payload },
      }: PayloadAction<WithId<Partial<ConversationState>>>,
    ) => {
      conversation[id] = {
        ...initialConversationState,
        ...payload,
      };
    },

    deleteConversation: (
      conversation,
      { payload: id }: PayloadAction<string>,
    ) => {
      delete conversation[id];
    },
  },

  extraReducers: {
    RESET_APP: () => ({
      [platform.playerID]: initialConversationState,
    }),
  },
});

export const conversationActions = actions;
export default reducer;

export async function awaitConversationFinished(id: string) {
  const { conversation } = StateObserver.getState();
  if (!conversation[id]?.activeScript) {
    await statePromise(
      ({ conversation }) =>
        !conversation[id] || !!conversation[id].activeScript,
    );
  }
  await statePromise(({ conversation }) => !conversation[id]?.activeScript);
}

export const isPetSpeaking = (id?: string) => {
  if (
    PopupMonitor.isOpen('popupDescriptiveScript') ||
    PopupMonitor.isOpen('popupSelectionalScript') ||
    PopupMonitor.isOpen('popupConversationScript') ||
    PopupMonitor.isOpen('popupSpeechBubble')
  ) {
    return true;
  }

  const { conversation } = StateObserver.getState();
  const petIds = id ? [id] : Object.keys(conversation);
  return petIds.some((petId) => conversation[petId]?.activeScript);
};

export async function startConversation(
  id: string,
  scene: DialogScenes,
  species: PetSpecies,
  petState: PetState,
) {
  const { setActiveDialog } = conversationActions;
  const state = StateObserver.getState();
  const { user } = state;
  const { getValidDialogs } = Conversation.getters(user);
  const now = StateObserver.now();

  const playerValidDialogs = getValidDialogs(
    scene,
    species,
    petState,
    user.conversation.answers,
  );

  StateObserver.dispatch(
    setActiveDialog({
      id,
      payload: getRandomItemFromArray(playerValidDialogs, Math.random).id,
    }),
  );

  await awaitConversationFinished(id);
}

export function getPlayerIdFromConversation(conversationId: string) {
  const { conversation } = StateObserver.getState();
  return (
    Object.keys(conversation).find(
      (key) => conversation[key].activeScript === conversationId,
    ) ?? platform.playerID
  );
}
